import { getKernel, getKernelLoader } from '../../../Services/KernelRegistry'
import { RouteKernelLoader } from '../../../Commands/Loaders/RouteKernelLoader'
import type { MenuItem, MenuItemInput } from '../../../Contracts/UI/Menu'
import { GetResourceTask } from '../../Resources/GetResourceTask'

/**
 * Опції для конфігурації функції GetMenuItems
 */
export interface GetMenuItemsOptions {
    /** Приховувати пункти меню для неіснуючих маршрутів замість показу помилки */
    hideNotFound?: boolean
    /** Показувати попередження в консолі */
    showWarnings?: boolean
    /** Користувацька функція для обробки відсутніх маршрутів */
    onRouteNotFound?: (routeKey: string) => MenuItem | null
}

/**
 * Отримує масив пунктів меню Ant Design з масиву ключів маршрутів та готових об'єктів меню
 *
 * Ця функція автоматично сканує маршрути системи через ядро (Kernel) і замінює
 * рядкові ключі маршрутів на відповідні об'єкти пунктів меню. Готові об'єкти
 * пунктів меню залишаються без змін.
 *
 * @param items - масив елементів, який може містити:
 *   - рядки (ключі маршрутів) - будуть замінені на об'єкти пунктів меню на основі знайдених маршрутів
 *   - об'єкти пунктів меню - залишаються як є
 * @param options - опції конфігурації для налаштування поведінки функції
 * @returns масив пунктів меню для компонента Ant Design Menu
 *
 * @example
 * Базове використання:
 * ```typescript
 * const menuItems = GetMenuItems([
 *   'home',  // ключ маршруту - буде замінено на об'єкт пункту меню
 *   'about', // ключ маршруту - буде замінено на об'єкт пункту меню
 *   {        // готовий об'єкт пункту меню - залишиться як є
 *     key: 'custom',
 *     label: 'Custom Item',
 *     icon: <CustomIcon />
 *   }
 * ]);
 * ```
 *
 * @example
 * Використання з опціями:
 * ```typescript
 * const menuItemsWithOptions = GetMenuItems([
 *   'home',
 *   'nonexistent-route'
 * ], {
 *   hideNotFound: true,    // приховати неіснуючі маршрути
 *   showWarnings: false,   // не показувати попередження
 *   onRouteNotFound: (key) => ({  // кастомна обробка відсутніх маршрутів
 *     key,
 *     label: `Маршрут ${key} в розробці`,
 *     disabled: true
 *   })
 * });
 * ```
 *
 * @example
 * Приклад структури повернутого об'єкта:
 * ```typescript
 * [
 *   {
 *     key: 'home',
 *     label: 'Головна',
 *     'data-route-path': '/',
 *     'data-route-key': 'home'
 *   },
 *   {
 *     key: 'custom',
 *     label: 'Custom Item',
 *     icon: <CustomIcon />
 *   }
 * ]
 * ```
 */
export const GetMenuItems = (
    items: MenuItemInput[],
    options: GetMenuItemsOptions = {}
): MenuItem[] => {
    const { hideNotFound = false, showWarnings = true, onRouteNotFound } = options

    // Валідація вхідних даних
    if (!Array.isArray(items)) {
        if (showWarnings) {
            console.warn('GetMenuItems: items має бути масивом')
        }
        return []
    }

    // Отримання екземплярів ядра та завантажувача маршрутів
    let routeLoader: RouteKernelLoader

    try {
        getKernel()
        routeLoader = getKernelLoader('RouteKernelLoader') || getKernelLoader('route')

        if (!routeLoader) {
            if (showWarnings) {
                console.warn('GetMenuItems: RouteKernelLoader не знайдено в ядрі')
            }
            return []
        }
    } catch (error) {
        if (showWarnings) {
            console.error('GetMenuItems: Помилка отримання ядра або завантажувача маршрутів:', error)
        }
        return []
    }

    const result: MenuItem[] = []

    // Рекурсивна функція для обробки елементів меню
    const processMenuItem = (item: MenuItemInput): MenuItem | null => {
        try {
            // Якщо елемент вже є об'єктом пункту меню
            if (typeof item === 'object' && item !== null) {
                // Рекурсивно обробляємо children, якщо вони є
                if (item.children && Array.isArray(item.children)) {
                    const processedChildren = item.children
                        .map(child => processMenuItem(child))
                        .filter((child): child is MenuItem => child !== null)
                    
                    if (processedChildren.length > 0) {
                        return {
                            ...item,
                            children: processedChildren
                        } as MenuItem
                    } else {
                        // Удаляем children если они пустые
                        const { children, ...restItem } = item
                        return restItem as MenuItem
                    }
                }
                return item as MenuItem
            }

            // Якщо елемент є рядком (ключ маршруту), шукаємо відповідний маршрут
            if (typeof item === 'string') {
                const route = routeLoader.findRouteByKey(item)
                
                if (route) {
                    // Створюємо пункт меню на основі знайденого маршруту
                    let menuItem: any = {
                        key: route.key || route.path,
                        label: route.config?.title || route.componentName.replace('Page', ''),
                        icon: route.config?.icon || null,
                        'data-route-path': route.path,
                        'data-route-key': route.key,
                    }

                    if( route.isResourceGenerated ) {
                        const resource = GetResourceTask(route.key || '')
                        if( resource ) {
                            menuItem.label = resource['pageConfig']?.title || resource?.title || menuItem.label
                            menuItem.icon = resource['pageConfig']?.icon || menuItem.icon
                        }
                    }

                    return menuItem as MenuItem
                } else {
                    // Маршрут не знайдено - обробляємо залежно від опцій
                    if (onRouteNotFound) {
                        // Використовуємо користувацьку функцію обробки
                        return onRouteNotFound(item)
                    } else if (!hideNotFound) {
                        // Показуємо fallback пункт меню з індикацією помилки
                        if (showWarnings) {
                            console.warn(`GetMenuItems: маршрут з ключем "${item}" не знайдено`)
                        }

                        return {
                            key: item,
                            label: `Маршрут "${item}" не знайдено`,
                            disabled: true,
                            style: { color: '#ff4d4f' },
                        } as MenuItem
                    }
                    // Якщо hideNotFound === true, повертаємо null
                    return null
                }
            }

            return null
        } catch (error) {
            // Обробка неочікуваних помилок при обробці окремого елемента
            if (showWarnings) {
                console.error(`GetMenuItems: Помилка обробки елемента "${item}":`, error)
            }

            // У разі помилки повертаємо fallback елемент, якщо не приховуємо помилки
            if (!hideNotFound) {
                return {
                    key: typeof item === 'string' ? item : 'error',
                    label: 'Помилка обробки елемента',
                    disabled: true,
                    style: { color: '#ff4d4f' },
                } as MenuItem
            }
            return null
        }
    }

    // Обробка кожного елемента в масиві
    for (const item of items) {
        const processedItem = processMenuItem(item)
        if (processedItem) {
            result.push(processedItem)
        }
    }

    return result
}
